import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormBuilder, FieldControl, Validators } from 'react-reactive-form';
import Select from 'react-select';
import { closePopupIcon, reviewIcon, deleteIcon, infoTooltipIcon } from '../assets/superAdminAssets';
import CreateCustomPlan from './CreateCustomPlan';
import { getSubscriptionPlanDetails, listSubscriptionPlans } from '../services/subscriptionPlan.service';
import { useAppContext } from '../contexts/AppContext';
import { Constants } from '../constants';
import { applicationPrefixValidator, getNormalizedValues, markAllControlsDirtyAndTouched, printInvalidControls } from '../helpers/form.helper';
import { getCompanyDetails, insertUpdateCompanyMaster, validateCompanyDomain } from '../services/company.service';
import { base64ToFile, capitalizeKeysDeep, convertToFormData, getBoolean } from '../helpers/index.helper';
import moment from 'moment';

const RequiredLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="required-label">
    {children} <span className="text-danger">*</span>
  </label>
);

const validateCompanyLogo = (control) => {
  const file = control.value;
  if (!file) {
    return null;
  }
  const maxSize = 2 * 1024 * 1024; // 2MB
  const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
  if (!allowedTypes.includes(file.type) || file.size > maxSize) {
    return { invalidFile: true };
  }
  return null;
};

function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}


const urlValidator = (control) => {
  const value = control.value;
  if (!value) return null;
  return isValidUrl(value) ? null : { invalidUrl: true };
};

const domainValidator = (control) => {
  const value = (control.value || '').trim();
  if (!value) return null;
  // Only letters, numbers, and hyphens allowed. "stage", "dev", and names ending with "-stage" or "-dev" are not allowed.
  const domainPattern = /^(?!stage$)(?!dev$)(?!.*[^a-zA-Z0-9](stage|dev)$)[a-zA-Z0-9-]+$/;
  return domainPattern.test(value?.toLowerCase()) ? null : { invalidDomain: true };
};

const socialDomainValidator = (allowedDomains) => (control) => {
  const rawValue = (control.value || '').trim();
  if (!rawValue) return null;

  if (!isValidUrl(rawValue)) {
    return { invalidUrl: true };
  }

  try {
    const hostname = new URL(rawValue).hostname.toLowerCase();
    const isAllowed = allowedDomains.some((domain) => {
      const d = domain.toLowerCase();
      return hostname === d || hostname.endsWith(`.${d}`);
    });

    return isAllowed ? null : { invalidDomain: true };
  } catch {
    return { invalidUrl: true };
  }
};

const CompanyDrawer = ({ show, onClose, onSubmit, companyId = '0' }) => {
  const { showLoader, hideLoader, showToaster, hideToaster, isLoading } = useAppContext();
  const [showAnimation, setShowAnimation] = useState(false);
  const [customPlanForm, setCustomPlanForm] = useState(null);
  const [subscriptionPlanList, setSubscriptionPlanList] = useState([]);
  const [subscriptionPlanListValue, setSubscriptionPlanListValue] = useState([]);
  const [plansLoadingMore, setPlansLoadingMore] = useState(false);
  const [domainName, setDomainName] = useState('');
  const logoInputRef = useRef(null);
  const [compnayNameValue, setCompnayNameValue] = useState('');
  const [planValue, setPlanValue] = useState(null);
  const [planDetails, setPlanDetails] = useState(null);

  const companyForm = useMemo(
    () =>
      FormBuilder.group({
        Id: [companyId || '0', Validators.required],
        CompanyName: ['', [Validators.required, Validators.maxLength(50)]],
        CompanyLogoData: ['', [Validators.required, validateCompanyLogo]],
        DomainName: ['', [Validators.required, domainValidator]],
        ApplicationNoPrefix: ['', [Validators.required, applicationPrefixValidator]],
        FirstName: ['', Validators.required],
        LastName: ['', [Validators.required, Validators.maxLength(50)]],
        Email: ['', [Validators.required, Validators.email]],
        SubscriptionPlanId: ['', Validators.required],
        FacebookLink: ['', [urlValidator, socialDomainValidator(['facebook.com'])]],
        TermsOfUseLink: ['', [urlValidator]],
        PolicyLink: ['', [urlValidator]],
        InstagramLink: ['', [urlValidator, socialDomainValidator(['instagram.com'])]],
        SecondaryBrandColor: ['#22315D', Validators.required],
        XLink: ['', [urlValidator, socialDomainValidator(['x.com', 'twitter.com'])]],
        FooterLink: ['', [urlValidator]],
        LinkedInLink: ['', [urlValidator, socialDomainValidator(['linkedin.com'])]],
        AccentColor: ['#F0F2FF', Validators.required],
        YouTubeLink: ['', [urlValidator, socialDomainValidator(['youtube.com', 'youtu.be'])]],
        PrimaryBrandColor: ['#4057E3', Validators.required],
        SupportEmail: ['', [Validators.email]],
        MissionOrVisionStatement: ['', [Validators.maxLength(500)]],
      }),
    []
  );

  const getFullDomainName = useCallback((domainName) => {
    return Constants.DOMAIN_SUFFIX
      ? `${domainName}${Constants.DOMAIN_SUFFIX}.${Constants.DEFAULT_DOMAIN_NAME}`
      : `${domainName}.${Constants.DEFAULT_DOMAIN_NAME}`;
  }, []);

  const getDomainName = useCallback((domainName) => {
    return domainName.replace(`${Constants.DOMAIN_SUFFIX}.${Constants.DEFAULT_DOMAIN_NAME}`, '').replace(`.${Constants.DEFAULT_DOMAIN_NAME}`, '');
  }, []);

  // Handle drawer animation
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        setShowAnimation(true);
      }, 50);
      return () => clearTimeout(timer);
    }
    setShowAnimation(false);
  }, [show]);

  useEffect(() => {
    if (subscriptionPlanListValue.length > 0) {
      let planList = subscriptionPlanListValue.filter((option) => !isNaN(option?.Status) && ((Number(getBoolean(option?.Status)) === 1 && Number(getBoolean(option?.IsDeleted)) !== 1 && Number(getBoolean(option?.IsCustom)) !== 1) || Number(option?.Id) === Number(planValue)));
      setSubscriptionPlanList(planList);
    } else {
      setSubscriptionPlanList([]);
    }
  },[subscriptionPlanListValue, planValue]);
  
  const fetchSubscriptionPlanDetails = async (planId) => {
    try {
      if (isNaN(planId) || Number(planId) <= 0) {
        setPlanDetails(null);
        return;
      }
      setPlansLoadingMore(true);
      const res = await getSubscriptionPlanDetails(planId);
      if (res.status === 200) {
        const data = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
        setPlanDetails(capitalizeKeysDeep(data));
      } else {
        setPlanDetails(null);
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      setPlanDetails(null);
      showToaster('Failed to fetch subscription plan details', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      setPlansLoadingMore(false);
    }
  };

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      setPlansLoadingMore(true);
      const res = await listSubscriptionPlans('', '', '', '', true);
      if (res?.status === 200) {
        let data = [];
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          data = Array.isArray(parsed) ? parsed : [];
        }
        setSubscriptionPlanListValue([{ Id: '0', PlanName: 'Create custom plan', Status: 1 }, ...data]);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch subscription plans', 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    } finally {
      setPlansLoadingMore(false);
    }
  }, [showLoader, hideLoader, showToaster, hideToaster]);


  const fetchCompanyDetails = useCallback(async (companyId) => {
    try {
      showLoader();
      const res = await getCompanyDetails(companyId);
      if (res?.status === 200) {
        if (res.resultObject) {
          let parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          setCompnayNameValue(parsed.companyName);
          setPlanValue(parsed.subscriptionPlanId);
          parsed.companyLogoData = base64ToFile(parsed.companyLogoData ? 'data:image/png;base64,' + parsed.companyLogoData : '', 'company_logo.png', 'image/png');
          parsed.domainName = getDomainName(parsed.domainName);
          parsed = capitalizeKeysDeep(parsed);
          companyForm.patchValue({
            ...parsed,
            PrimaryBrandColor: parsed?.PrimaryBrandColor || '#4057E3',
            SecondaryBrandColor: parsed?.SecondaryBrandColor || '#22315D',
            AccentColor: parsed?.AccentColor || '#F0F2FF'
          });
          if (!isNaN(parsed.SubscriptionPlanId) && Number(parsed.SubscriptionPlanId) > 0) {
            fetchSubscriptionPlanDetails(parsed.SubscriptionPlanId);
          }
        }
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch company details', 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }, [companyId]);

  const handleDomainValidation = useCallback(async (domainName) => {
    let domainErrors = { ...companyForm.get('DomainName').errors };
    try {
      if (isLoading) return;
      const res = await validateCompanyDomain(getFullDomainName(domainName));
      if (res?.status != 200 || res?.resultObject?.toLowerCase() != "available") {
        companyForm.get('DomainName').setErrors({ ...domainErrors, domainTaken: true });
      } else {
        companyForm.get('DomainName').updateValueAndValidity();
        return true;
      }
    } catch (error) {
      console.log(error);
      if (error?.response?.status == 400) {
        companyForm.get('DomainName').setErrors({ ...domainErrors, domainTaken: true });
        return false;
      }
      showToaster('Failed to validate domain', 'error');
      setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
    }
  }, [getFullDomainName]);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isNaN(companyId) && companyId != '0') {
        fetchCompanyDetails(companyId);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [companyId, fetchCompanyDetails]);

  useEffect(() => {
    if (domainName) {
      handleDomainValidation(domainName);
    } else {
      companyForm.get('DomainName').updateValueAndValidity();
    }
  }, [domainName]);

  if (!show) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      showLoader();
      markAllControlsDirtyAndTouched(companyForm);
      if (companyForm.value.SubscriptionPlanId == '0') {
        markAllControlsDirtyAndTouched(customPlanForm);
      }
      if (!companyForm.valid || (companyForm.value.SubscriptionPlanId == '0' && !customPlanForm.valid)) {
        printInvalidControls(companyForm);
        if (companyForm.value.SubscriptionPlanId == '0') {
          printInvalidControls(customPlanForm);
        }
        return;
      }
      const data = getNormalizedValues(companyForm.value);
      data.CompanyId = 0;
      data.DomainName = getFullDomainName(data.DomainName);
      data.CompanyLogoData = companyForm.value.CompanyLogoData ? companyForm.value.CompanyLogoData : null;
      if (companyForm.value.SubscriptionPlanId == '0') {
        data.SubscriptionPlan = getNormalizedValues(customPlanForm.value);
      }
      data.companyLogo = data.CompanyLogoData;
      const formData = convertToFormData(data);
      const res = await insertUpdateCompanyMaster(formData);
      if (res?.status === 200) {
        showToaster(res.message, 'success');
        if (typeof onSubmit === 'function') {
          onSubmit({
            company: data,
            plan: companyForm.value.SubscriptionPlanId == '0' ? data.SubscriptionPlan : null,
          });
        }
        onClose();
      } else {
        showToaster(res.message, 'error');
      }
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } catch (error) {
      console.log(error);
      showToaster('Failed to insert update company', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div
        className={`modal-backdrop fade ${showAnimation ? 'show' : ''}`}
        onClick={onClose}
        style={{ zIndex: 1040 }}
      ></div>
      <div
        className={`modal fade-right d-block drawer-modal custom-full-width-drawer-size ${showAnimation ? 'show' : ''
          }`}
        tabIndex={-1}
        role="dialog"
        style={{ zIndex: 1050 }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog">
          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              <div className="drawer-head d-flex justify-content-between">
                {companyId == '0' ? <h3>Add a new company</h3> : <h3>Edit {compnayNameValue}</h3>}
                <button
                  type="button"
                  className="close border-none"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <span aria-hidden="true">
                    <img src={closePopupIcon} alt="" />
                  </span>
                </button>
              </div>
              <div className="drawer-body">
                <div className="row">
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('CompanyName')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showMaxLength = touched && hasError('maxlength');
                        return (
                          <div
                            className={`form-group ${showRequired || showMaxLength ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="company_name">Company Name</RequiredLabel>
                            <input
                              type="text"
                              className="form-control"
                              id="company_name"
                              placeholder="Enter company name"
                              maxLength={50}
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Company name is required</span>
                            )}
                            {!showRequired && showMaxLength && (
                              <span className="error-message">
                                Company name cannot exceed 50 characters
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('CompanyLogoData')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidFile');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="company_logo">Company Logo</RequiredLabel>
                            <div className="upload-box">
                              <input
                                type="file"
                                id="company_logo"
                                name="company_logo"
                                accept="image/png,image/jpeg,image/gif"
                                style={{
                                  opacity: 0,
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  height: '100%',
                                  width: '100%',
                                  cursor: 'pointer',
                                }}
                                ref={logoInputRef}
                                onChange={(e) => {
                                  const file = e.target.files && e.target.files[0];
                                  setValue(file || null);
                                }}
                              />
                              <div className="upload-text">
                                <span>
                                  Drop file here or{' '}
                                  <button type="button" id="upload-button">
                                    browse
                                  </button>
                                </span>
                              </div>
                            </div>
                            {value && value.name && (
                              <div className="uploaded-file d-flex align-items-center fs-12">
                                <span className="me-2">{value.name}</span>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 me-2"
                                  onClick={() => {
                                    // Basic browser preview for image files
                                    const url = URL.createObjectURL(value);
                                    window.open(url, '_blank');
                                  }}
                                  aria-label="View logo"
                                >
                                  <img src={reviewIcon} alt="View" height={16} width={16} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 remove-file"
                                  onClick={() => {
                                    if (logoInputRef.current) {
                                      logoInputRef.current.value = '';
                                    }
                                    setValue(null);
                                  }}
                                  aria-label="Remove logo"
                                >
                                  <img src={deleteIcon} alt="Remove" height={16} width={16} />
                                </button>
                              </div>
                            )}
                            <div className="input-helper">PNG, JPG, or GIF. Max 2MB</div>
                            {showRequired && (
                              <span className="error-message">Company logo is required</span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Invalid file. Only PNG, JPG, or GIF up to 2MB allowed.
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('DomainName')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidDomain');
                        const domainTaken = hasError('domainTaken');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="company_domain">Company Domain</RequiredLabel>
                            <div className="input-group mb-2 me-sm-2">
                              <input
                                type="text"
                                className="form-control"
                                id="company_domain"
                                placeholder="Enter company domain"
                                value={value || ''}
                                onChange={(e) => {
                                  const next = (e.target.value || '').replace(/\s+/g, '');
                                  setValue(next);
                                }}
                                onBlur={(e) => {
                                  const next = (e.target.value || '').replace(/\s+/g, '');
                                  setDomainName(next);
                                }}
                              />
                              <div className="input-group-prepend">
                                <div className="input-group-text">{Constants.DOMAIN_SUFFIX ? Constants.DOMAIN_SUFFIX : ''}.{Constants.DEFAULT_DOMAIN_NAME}</div>
                              </div>
                            </div>
                            {showRequired && (
                              <span className="error-message">Company domain is required</span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Only letters, numbers, and hyphens (-) are allowed. "stage", "dev" and names ending with special characters followed by "stage", "dev" (e.g., -stage, .stage, .dev, -dev) are not allowed.
                              </span>
                            )}
                            {!showRequired && domainTaken && (
                              <span className="error-message">
                                Domain is already taken. Please enter a different domain.
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('ApplicationNoPrefix')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidApplicationPrefix');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="application_no_prefix">
                              Application no. Prefix
                            </RequiredLabel>
                            <input
                              type="text"
                              className="form-control"
                              id="application_no_prefix"
                              placeholder="Enter application no. prefix"
                              value={value || ''}
                              onChange={(e) => {
                                if (/^[a-zA-Z0-9]{0,5}$/.test(e.target.value)) {
                                  setValue(e.target.value);
                                }
                              }}
                            />
                            {showRequired && (
                              <span className="error-message">
                                Application no. prefix is required
                              </span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Application no. prefix must be 2 to 5 characters long and can only contain letters and numbers
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('PolicyLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidUrl');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="company_policy_link">
                              Company Policy Link
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="company_policy_link"
                              placeholder="Enter company policy link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">
                                Company policy link is required
                              </span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('FooterLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidUrl');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="company_footer_link">
                              Company Footer Link
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="company_footer_link"
                              placeholder="Enter company footer link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">
                                Company footer link is required
                              </span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('TermsOfUseLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalid = touched && hasError('invalidUrl');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalid ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="company_terms_of_use_link">
                              Company Terms Of Use Link
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="company_terms_of_use_link"
                              placeholder="Enter company terms of use link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">
                                Company terms of use link is required
                              </span>
                            )}
                            {!showRequired && showInvalid && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('SupportEmail')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showEmail = touched && hasError('email');
                        return (
                          <div
                            className={`form-group ${showRequired || showEmail ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="email_id">Support Email</label>
                            <input
                              type="text"
                              className="form-control"
                              id="support_email"
                              placeholder="Enter support email address"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Support email is required</span>
                            )}
                            {!showRequired && showEmail && (
                              <span className="error-message">Enter a valid support email</span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('MissionOrVisionStatement')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showMaxLength = touched && hasError('maxlength');
                        return (
                          <div
                            className={`form-group ${showRequired || showMaxLength ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="mission_or_vision_statement">Mission or Vision Statement</label>
                            <input
                              type="text"
                              className="form-control"
                              id="mission_or_vision_statement"
                              placeholder="Enter mission or vision statement"
                              maxLength={500}
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Mission or vision statement is required</span>
                            )}
                            {!showRequired && showMaxLength && (
                              <span className="error-message">
                                Mission or vision statement cannot exceed 500 characters
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>

                <hr className="lm-divider" />
                <h4 className="lm-card-heading" style={{ marginBottom: '20px', fontSize: '16px' }}>
                  Social Media Details
                </h4>
                <div className="row">
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('FacebookLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalidUrl = touched && hasError('invalidUrl');
                        const showInvalidDomain = touched && hasError('invalidDomain');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalidUrl || showInvalidDomain ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="facebook_link">Facebook Link</label>
                            <input
                              type="text"
                              className="form-control"
                              id="facebook_link"
                              placeholder="Enter Facebook link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Facebook link is required</span>
                            )}
                            {!showRequired && showInvalidUrl && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                            {!showRequired && !showInvalidUrl && showInvalidDomain && (
                              <span className="error-message">
                                Enter a valid Facebook URL (must contain facebook.com)
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('InstagramLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalidUrl = touched && hasError('invalidUrl');
                        const showInvalidDomain = touched && hasError('invalidDomain');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalidUrl || showInvalidDomain ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="instagram_link">Instagram Link</label>
                            <input
                              type="text"
                              className="form-control"
                              id="instagram_link"
                              placeholder="Enter Instagram link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Instagram link is required</span>
                            )}
                            {!showRequired && showInvalidUrl && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                            {!showRequired && !showInvalidUrl && showInvalidDomain && (
                              <span className="error-message">
                                Enter a valid Instagram URL (must contain instagram.com)
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('XLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalidUrl = touched && hasError('invalidUrl');
                        const showInvalidDomain = touched && hasError('invalidDomain');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalidUrl || showInvalidDomain ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="x_link">X Link</label>
                            <input
                              type="text"
                              className="form-control"
                              id="x_link"
                              placeholder="Enter X link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">X link is required</span>
                            )}
                            {!showRequired && showInvalidUrl && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                            {!showRequired && !showInvalidUrl && showInvalidDomain && (
                              <span className="error-message">
                                Enter a valid X URL (must contain x.com or twitter.com)
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('LinkedInLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalidUrl = touched && hasError('invalidUrl');
                        const showInvalidDomain = touched && hasError('invalidDomain');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalidUrl || showInvalidDomain ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="linkedin">LinkedIn Link</label>
                            <input
                              type="text"
                              className="form-control"
                              id="linkedin"
                              placeholder="Enter LinkedIn link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">LinkedIn link is required</span>
                            )}
                            {!showRequired && showInvalidUrl && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                            {!showRequired && !showInvalidUrl && showInvalidDomain && (
                              <span className="error-message">
                                Enter a valid LinkedIn URL (must contain linkedin.com)
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('YouTubeLink')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showInvalidUrl = touched && hasError('invalidUrl');
                        const showInvalidDomain = touched && hasError('invalidDomain');
                        return (
                          <div
                            className={`form-group ${showRequired || showInvalidUrl || showInvalidDomain ? 'error-field' : ''
                              }`}
                          >
                            <label htmlFor="youtube_link">YouTube Link</label>
                            <input
                              type="text"
                              className="form-control"
                              id="youtube_link"
                              placeholder="Enter YouTube link"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">YouTube link is required</span>
                            )}
                            {!showRequired && showInvalidUrl && (
                              <span className="error-message">
                                Enter a valid URL starting with http:// or https://
                              </span>
                            )}
                            {!showRequired && !showInvalidUrl && showInvalidDomain && (
                              <span className="error-message">
                                Enter a valid YouTube URL (must contain youtube.com or youtu.be)
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>

                <hr className="lm-divider" />

                <h4 className="lm-card-heading" style={{ marginBottom: '20px', fontSize: '16px' }}>
                  Admin Details
                </h4>

                <div className="row">
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('FirstName')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showMaxLength = touched && hasError('maxlength');
                        return (
                          <div
                            className={`form-group ${showRequired || showMaxLength ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="first_name">First Name</RequiredLabel>
                            <input
                              type="text"
                              className="form-control"
                              id="first_name"
                              placeholder="Enter first name"
                              maxLength={50}
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">First name is required</span>
                            )}
                            {!showRequired && showMaxLength && (
                              <span className="error-message">
                                First name cannot exceed 50 characters
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('LastName')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showMaxLength = touched && hasError('maxlength');
                        return (
                          <div
                            className={`form-group ${showRequired || showMaxLength ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="last_name">Last Name</RequiredLabel>
                            <input
                              type="text"
                              className="form-control"
                              id="last_name"
                              placeholder="Enter last name"
                              maxLength={50}
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Last name is required</span>
                            )}
                            {!showRequired && showMaxLength && (
                              <span className="error-message">
                                Last name cannot exceed 50 characters
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('Email')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showRequired = touched && hasError('required');
                        const showEmail = touched && hasError('email');
                        return (
                          <div
                            className={`form-group ${showRequired || showEmail ? 'error-field' : ''
                              }`}
                          >
                            <RequiredLabel htmlFor="email_id">Email ID</RequiredLabel>
                            <input
                              type="text"
                              className="form-control"
                              id="email_id"
                              placeholder="Enter email address"
                              value={value || ''}
                              onChange={(e) => setValue(e.target.value)}
                            />
                            {showRequired && (
                              <span className="error-message">Email is required</span>
                            )}
                            {!showRequired && showEmail && (
                              <span className="error-message">Enter a valid email</span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>

                <hr className="lm-divider" />

                <h4 className="lm-card-heading" style={{ marginBottom: '20px', fontSize: '16px' }}>
                  Subscription Plan
                </h4>
                <div className="row">

                  <FieldControl
                    strict={false}
                    control={companyForm.get('SubscriptionPlanId')}
                    render={({ value, setValue, touched, hasError }) => {
                      const showError = touched && hasError('required');
                      const isCustom = value != null && String(value) === '0';
                      return (
                        <>
                          <div className="col-md-4">
                            <div className={`form-group ${showError ? 'error-field' : ''}`}>
                              <RequiredLabel htmlFor="select_plan">Select Plan</RequiredLabel>
                              <Select
                                inputId="select_plan"
                                className="basic-single"
                                classNamePrefix="select"
                                placeholder="Select a plan"
                                isLoading={plansLoadingMore}
                                options={subscriptionPlanList}
                                getOptionLabel={(opt) => getBoolean(opt.IsCustom) ? 'Custom Plan' : opt.PlanName}
                                getOptionValue={(opt) => String(opt.Id)}
                                value={
                                  value
                                    ? subscriptionPlanList.find(
                                      (opt) => String(opt.Id) === String(value)
                                    ) || null
                                    : null
                                }
                                onChange={(option) => {
                                  setValue(option ? option.Id : '');
                                  fetchSubscriptionPlanDetails(option.Id);
                                }}
                              />
                              {showError && (
                                <span className="error-message">
                                  Subscription plan is required
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-3">
                            {
                              !plansLoadingMore && (
                                <CreateCustomPlan
                                  onFormInit={setCustomPlanForm}
                                  data={planDetails}
                                  disabled={!isCustom}
                                  showTrialEndDetail={planValue != companyForm.value.SubscriptionPlanId}
                                  isCustom={isCustom}
                                  selectedPlan={subscriptionPlanList.find((opt) => String(opt.Id) === String(value))} />
                              )
                            }
                          </div>
                        </>
                      );
                    }}
                  />
                </div>
                <hr className="lm-divider" />

                <h4 className="lm-card-heading" style={{ marginBottom: '20px', fontSize: '16px' }}>
                  Theme Colors
                </h4>
                <div className="row">
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('PrimaryBrandColor')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <RequiredLabel htmlFor="primary_color">
                              Primary Brand Color
                            </RequiredLabel>
                            <span
                              className="lm-tooltip ms-1"
                              data-tooltip="This color will be used for the buttons and links"
                              data-tooltip-pos="up"
                              data-tooltip-length="large"
                            >
                              <img src={infoTooltipIcon} alt="" />
                            </span>
                            <input
                              type="color"
                              className="form-control"
                              id="primary_color"
                              value={value || '#4057E3'}
                              onChange={(e) => setValue(e.target.value)}
                              style={{ height: '45px', padding: '4px 8px', cursor: 'pointer' }}
                            />
                            {showError && (
                              <span className="error-message">
                                Primary brand color is required
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('SecondaryBrandColor')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <RequiredLabel htmlFor="secondary_color">
                              Secondary Brand Color
                            </RequiredLabel>
                            <span
                              className="lm-tooltip ms-1"
                              data-tooltip="This color will be used for the Sidenavigation and focused borders"
                              data-tooltip-pos="up"
                              data-tooltip-length="large"
                            >
                              <img src={infoTooltipIcon} alt="" />
                            </span>
                            <input
                              type="color"
                              className="form-control"
                              id="secondary_color"
                              value={value || '#22315D'}
                              onChange={(e) => setValue(e.target.value)}
                              style={{ height: '45px', padding: '4px 8px', cursor: 'pointer' }}
                            />
                            {showError && (
                              <span className="error-message">
                                Secondary brand color is required
                              </span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <FieldControl
                      strict={false}
                      control={companyForm.get('AccentColor')}
                      render={({ value, setValue, touched, hasError }) => {
                        const showError = touched && hasError('required');
                        return (
                          <div className={`form-group ${showError ? 'error-field' : ''}`}>
                            <RequiredLabel htmlFor="accent_color">Accent Color</RequiredLabel>
                            <span
                              className="lm-tooltip ms-1"
                              data-tooltip="This color will be used for the background and borders"
                              data-tooltip-pos="up"
                              data-tooltip-length="medium"
                            >
                              <img src={infoTooltipIcon} alt="" />
                            </span>
                            <input
                              type="color"
                              className="form-control"
                              id="accent_color"
                              value={value || '#F0F2FF'}
                              onChange={(e) => setValue(e.target.value)}
                              style={{ height: '45px', padding: '4px 8px', cursor: 'pointer' }}
                            />
                            {showError && (
                              <span className="error-message">Accent color is required</span>
                            )}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>

                <div className="sticky-btn-footer d-flex justify-content-end w-100 gap-15">
                  <button type="button" className="lm-button2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="lm-button1 ml-auto">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CompanyDrawer;

